<script setup lang="ts">
import type { IEventPublisherDataDTO } from '~/shared/api/client/events/events-publisher.dto';
import { mapPublisherEvents } from '~/shared/api/client/events/events-publisher.dto';
import { getEventRoute } from '~/shared/lib/getEventRoute';
import {
  CLIENT_EVENTS_PARTNERS_URL,
  CLIENT_EVENTS_PARTNERS_KEY,
} from '~/shared/api/client/publisher/publisher.const';
import { useCustomFetch } from '~/shared/lib/useCustomFetch';
import { getMonthName } from '~/shared/utils/formatDate';
import { parseLocalDateTime } from '~/shared/utils/parseLocalDateTime';
import { getEventCardStub } from '~/shared/lib/getEventStubImage';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_EVENTS_PARTNERS } from '~/shared/lib/crop-ratio.const';
import EventPartnerAdOverlay from './EventPartnerAdOverlay.vue';

const { data } = useCustomFetch<IEventPublisherDataDTO[]>(
  CLIENT_EVENTS_PARTNERS_URL,
  {
    key: CLIENT_EVENTS_PARTNERS_KEY,
  },
);

const events = computed(() => {
  if (!data.value) {
    return [];
  }
  return mapPublisherEvents(data.value)
    .slice(0, 5)
    .map((item) => ({
      ...item,
      image: resizeCropSlot(
        item.imageUrl || getEventCardStub(item.event.id),
        CROP_RATIO_EVENTS_PARTNERS,
        0,
        { width: 72 },
      ),
    }));
});

function formatEventDayMonth(isoString: string, monthOnly: boolean): string {
  const { day, month } = parseLocalDateTime(isoString);
  if (monthOnly) {
    return getMonthName(month - 1, 'nominative');
  }
  return `${day} ${getMonthName(month - 1)}`;
}
</script>

<template>
  <div v-if="events.length" :class="$style.root">
    <div :class="$style.header">Партнерские мероприятия</div>
    <div :class="$style.list">
      <NuxtLink
        v-for="item in events"
        :key="item.event.id"
        :to="getEventRoute(item.event)"
        :class="$style.item"
      >
        <div :class="$style.item__textBlock">
          <span :class="$style.date">{{
            formatEventDayMonth(item.event.start_time, item.event.month_only)
          }}</span>
          <span :class="$style.name">{{ item.event.name }}</span>
        </div>
        <div :class="$style.item__right">
          <div :class="$style.item__imageWrap">
            <img
              :src="item.image.src"
              :srcset="item.image.srcset"
              alt=""
              :class="$style.image"
            />
          </div>
          <EventPartnerAdOverlay
            :erid="item.event.erid"
            :company="item.event.company_information"
          />
        </div>
      </NuxtLink>
    </div>
  </div>
</template>

<style module lang="scss">
.root {
  display: flex;
  flex-direction: column;
  background-color: var(--color-gray-bg);
}

.header {
  @include text_body2_medium;
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 var(--space-2-5x);
  background-color: var(--color-white);
  border: 1px solid var(--color-gray-line);
  border-left: none;
  border-right: none;
  color: var(--color-black-1);
}

.list {
  display: flex;
  flex-direction: column;
}

.item {
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: var(--space-2-5x);
  background-color: var(--color-white);
  border-bottom: 1px solid var(--color-gray-line);
  text-decoration: none;
  color: var(--color-black-1);
  cursor: default;
}

.item__textBlock {
  display: flex;
  flex-direction: column;
  gap: var(--space-1x);
  flex: 1;
  min-width: 0;
  padding-right: var(--space-2x);
  cursor: pointer;
}

.date {
  @include text_caption2_caps;
  color: var(--color-event-accent);
}

.name {
  @include text_body2;
  @include line_clamp(3);
  color: var(--color-black-1);
}

.item__right {
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: 72px;
  margin-bottom: -14px;
  cursor: pointer;
}

.item__imageWrap {
  height: 64px;
  overflow: hidden;
}

.label {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: 1px;
  color: var(--color-gray-text);
  text-align: right;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}
</style>
